import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { Header } from "../../components/layout/Header";
import { EpisodeRow } from "../../components/episodes/EpisodeRow";
import { LinkButton } from "../../components/common";
import type { EpisodeListData } from "../../routes/loaders";
import type { Role } from "../../types/user";
import type { EpisodeStatus } from "../../types/api";
import styles from "./EpisodeListPage.module.css";

const STATUS_FILTERS: { value: EpisodeStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
];

const PAGE_SIZE = 20;

export default function EpisodeListPage() {
  const { episodes, podcast, organizationId, pagination } = useLoaderData() as EpisodeListData;
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStatus = (searchParams.get("status") as EpisodeStatus | null) || "all";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const userRole: Role | undefined = user?.organizations.find(
    (org) => org.id === organizationId,
  )?.role;

  const canCreateEpisode = userRole === "owner" || userRole === "admin";

  const handleStatusChange = (status: EpisodeStatus | "all") => {
    const params = new URLSearchParams(searchParams);
    if (status === "all") {
      params.delete("status");
    } else {
      params.set("status", status);
    }
    params.delete("page"); // Reset to page 1 when filter changes
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) {
      params.delete("page");
    } else {
      params.set("page", page.toString());
    }
    setSearchParams(params);
  };

  const totalPages = Math.ceil(pagination.total / PAGE_SIZE);

  return (
    <>
      <Header />
      <div className="container">
      <div className={styles.pageHeader}>
        <div>
          <h1>{podcast.name}</h1>
          <div className={styles.breadcrumb}>
            <Link to="/dashboard" className={styles.breadcrumbLink}>
              Dashboard
            </Link>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>Episodes</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Link
            to={`/organizations/${organizationId}/podcasts/${podcast.id}/analytics`}
            className={styles.settingsLink}
          >
            Analytics
          </Link>
          <Link
            to={`/organizations/${organizationId}/podcasts/${podcast.id}/subscribers`}
            className={styles.settingsLink}
          >
            Subscribers
          </Link>
          <Link
            to={`/organizations/${organizationId}/podcasts/${podcast.id}/settings`}
            className={styles.settingsLink}
          >
            Settings
          </Link>
          {canCreateEpisode && (
            <LinkButton
              to={`/organizations/${organizationId}/podcasts/${podcast.id}/episodes/create`}
              variant="primary"
            >
              Create Episode
            </LinkButton>
          )}
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.statusTabs}>
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => handleStatusChange(filter.value)}
              className={`${styles.statusTab} ${currentStatus === filter.value ? styles.active : ""}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <span className={styles.episodeCount}>
          {pagination.total} {pagination.total === 1 ? "episode" : "episodes"}
        </span>
      </div>

      {episodes.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="10 8 16 12 10 16 10 8" />
            </svg>
          </div>
          {currentStatus === "all" ? (
            <>
              <h2>No episodes yet</h2>
              <p>Create your first episode to start publishing.</p>
              {canCreateEpisode && (
                <LinkButton
                  to={`/organizations/${organizationId}/podcasts/${podcast.id}/episodes/create`}
                  variant="primary"
                >
                  Create Your First Episode
                </LinkButton>
              )}
            </>
          ) : (
            <>
              <h2>No {currentStatus} episodes</h2>
              <p>There are no episodes with "{currentStatus}" status.</p>
              <button
                type="button"
                onClick={() => handleStatusChange("all")}
                className={styles.showAllButton}
              >
                Show All Episodes
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className={styles.episodeList}>
            {episodes.map((episode) => (
              <EpisodeRow
                key={episode.id}
                episode={episode}
                organizationId={organizationId}
                podcastId={podcast.id}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={styles.pageButton}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={styles.pageButton}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}
